import React, { useCallback, useEffect } from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";

import SelectVariantIconButton from "@/shared/components/Select/SelectVariantIconButton";
import Typography from "@/shared/components/Typography/Typography";

import EgwWebFont from "src/assets/EgwWebFont";
import { searchActions } from "@/store/actions";
import { isSameBook } from "@/shared/utils/content/content";
import { selectorMainTreeDictionaries } from "@/shared/store/mainTree/selectors";
import { selectorSearchResultsDictionary } from "@/shared/store/search/selectors";
import { isParaHeader } from "@/shared/utils/reader/reader";
import { selectorLoaderById } from "@/shared/store/system/selectors";
import { LOADER_ID_DICTIONARY_LOAD } from "@/shared/store/search/constants";
import useDisableOverscroll from "@/shared/hooks/useDisableOverscroll";
import { selectorSetting } from "@/shared/store/settings/selectors";
import { settingsActions } from "@/store/actions/settings.actions";
import { useReaderContext } from "@/components/Reader/Reader";
import useRefData from "@/shared/hooks/useRefData";
import { SETTING } from "@/shared/utils/settings/constants";

import styles from "./dictionary-popup.module.scss";

const Popup = dynamic(() => import("@/shared/components/Popup/Popup"), {
  ssr: false,
});

const DICTIONARY_POPUP_CLASSNAME = "dictionary-popup";

const DictionaryPopup = ({
  search,
  isShown,
  anchorEl,
  contentWrapElRect,
  onHidePopup,
  className,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const dictionaries = useSelector(selectorMainTreeDictionaries);
  const searchDictionary = useSelector(selectorSearchResultsDictionary);
  const dictionaryFilter = useSelector(selectorSetting(SETTING.dictionaryFilter.id));
  const isLoading = useSelector((state) => selectorLoaderById(state, LOADER_ID_DICTIONARY_LOAD));
  useDisableOverscroll(DICTIONARY_POPUP_CLASSNAME);

  const { updatePanel, scrollParaIntoView, panelsIds } = useReaderContext();

  const refData = useRefData({ panelsIds });

  // Search for a word on {search} change.
  useEffect(() => {
    if (!search) {
      return;
    }

    dispatch(searchActions.searchDictionary(search, dictionaryFilter));
  }, [search, dictionaryFilter]);

  const changeDictionaryFilter = useCallback((key) => {
    dispatch(settingsActions.updateSettings(SETTING.dictionaryFilter.id, key));
  }, []);

  const handleClickDictionaryItem = (event, item) => {
    event.preventDefault();

    const { panelsIds } = refData.current;
    const idItem = item.para_id;

    const indexPanelWithItem = panelsIds.findIndex((p) => isSameBook(p, idItem));

    if (indexPanelWithItem === -1) {
      updatePanel(1, idItem);
    } else {
      if (panelsIds[indexPanelWithItem] === idItem) {
        scrollParaIntoView(indexPanelWithItem, idItem);
      } else {
        updatePanel(indexPanelWithItem, idItem);
      }
    }

    onHidePopup();
  };

  const handleMouseUp = (event) => {
    event.stopPropagation();
  };

  const renderResults = () => {
    if (isLoading) {
      return (
        <Typography variant="body4" color="default">
          {t("loading")}…
        </Typography>
      );
    }

    if (!searchDictionary.length) {
      return (
        <Typography variant="body4" color="default">
          {t("word_not_found_in_dictionary")}
        </Typography>
      );
    }

    return searchDictionary.map((item) => {
      return (
        <span
          key={item.para_id}
          onClick={(event) => handleClickDictionaryItem(event, item)}
          className={styles["dictionary-item-wrap"]}>
          <Typography
            color={isParaHeader(item) ? null : "default"}
            variant="body4"
            className={styles[item.element_subtype]}
            dangerouslySetInnerHTML={{ __html: item.content }}
          />
        </span>
      );
    });
  };

  return (
    <Popup
      contentProps={{
        className: classNames(className, "reference", styles["dictionary-popup-wrap"]),
        onMouseUp: handleMouseUp,
        onTouchEnd: handleMouseUp,
      }}
      isContentDynamic
      isShown={isShown}
      anchorEl={anchorEl}
      boundaryRect={contentWrapElRect}
      onHide={onHidePopup}
    >
      <div className={styles[DICTIONARY_POPUP_CLASSNAME]}>
        <div className={styles["popup-header"]}>
          <div className={styles["header-input"]}>
            <div className={styles["input-title"]}>{t("dictionary")}</div>
            <div className={styles["input-tools"]}>
              <SelectVariantIconButton
                icon={EgwWebFont.dictionary}
                value={parseInt(dictionaryFilter) || dictionaryFilter}
                options={dictionaries}
                onChange={changeDictionaryFilter}
              />
            </div>
          </div>
          <hr />
        </div>
        <div className={styles["content-wrap"]}>{renderResults()}</div>
      </div>
    </Popup>
  );
};

DictionaryPopup.propTypes = {
  isShown: PropTypes.bool,
  anchorEl: PropTypes.any,
  contentWrapElRect: PropTypes.object,
  onHidePopup: PropTypes.func,
  listTranslations: PropTypes.array,
  search: PropTypes.string,
  className: PropTypes.string,
};

export default DictionaryPopup;
